import { useContext, useState } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "../../services/toastService";
import { useValidation } from "../../hooks/useValidation";
import { createPaymentMethodSchema } from "../../validation/paymentMethodSchema";
import { paymentMethodService } from "../../services/paymentMethodService";
import BackButton from "../../components/common/BackButton";

const PaymentMethodForm = () => {
  const { t } = useContext(LocaleContext);
  const location = useLocation();
  const navigate = useNavigate();
  const record = location.state?.paymentMethod;
  const isEditMode = Boolean(record?.id);
  const [btnLoading, setBtnLoading] = useState(false);

  const initialForm = {
    provider: record?.provider || "",
    available: record ? record.available : true,
  };

  const v = useValidation();
  const schema = createPaymentMethodSchema(v, t, isEditMode);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialForm,
    resolver: yupResolver(schema),
  });

  const handleClear = () => {
    reset(initialForm);
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        provider: values.provider,
        available: values.available,
      };
      if (isEditMode) {
        await updateAsync(payload);
      } else {
        await addAsync(payload);
      }
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.error(error);
    }
  };

  const addAsync = async (payload) => {
    try {
      setBtnLoading(true);
      const response = await paymentMethodService.create(payload);
      if (!response.isSuccess) {
        response.key ? toast.error(t(response.key)) : toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        return;
      }
      toast.success(t("recordAddedSuccessfully"));
      navigate("/payment-methods");
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.error(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const updateAsync = async (payload) => {
    try {
      setBtnLoading(true);
      const response = await paymentMethodService.update(record.id, payload);
      if (!response.isSuccess) {
        response.key ? toast.error(t(response.key)) : toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        return;
      }
      toast.success(t("recordUpdatedSuccessfully"));
      navigate("/payment-methods");
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.error(error);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <>
      <section className="payment-method-form">
        <div className="default-heading">{t("paymentMethods")}</div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="my-card">
            <div className="my-card-header d-flex justify-content-between align-items-center">
              {isEditMode ? (
                <>
                  <i className="fas fa-edit"></i> {t("editPaymentMethod")}
                </>
              ) : (
                <>
                  <i className="fas fa-plus plus-icon"></i> {t("addNewPaymentMethod")}
                </>
              )}
              <BackButton />
            </div>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">
                    {t("provider")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("provider")}
                    className="form-control"
                    placeholder={t("provider")}
                  />
                  {errors.provider && (
                    <p className="text-danger">{errors.provider.message}</p>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("status")}</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="payment-method-available"
                      {...register("available")}
                    />
                    <label className="form-check-label" htmlFor="payment-method-available">
                      {t("available")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 gap-2 d-flex justify-content-end">
              {!btnLoading && (
                <button type="submit" className="btn-default">
                  {t("save")}
                </button>
              )}
              {btnLoading && (
                <button type="button" className="btn-default">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={handleClear}>
                {t("reset")}
              </button>
            </div>
          </div>
        </form>
      </section>
    </>
  );
};

export default PaymentMethodForm;
